import {
  useEffect,
  useRef,
  useSyncExternalStore,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";
import { ChatComposer } from "@altek/ui/components/chat/ChatComposer";
import { ChatPanel } from "@altek/ui/components/chat/ChatPanel";
import { Banner } from "@altek/ui/components/feedback/Banner";
import { ConfirmDialog } from "@altek/ui/components/feedback/ConfirmDialog";
import { EmptyState } from "@altek/ui/components/feedback/EmptyState";
import { formatCount } from "@altek/ui/lib/format/number";
import { conversationTitle } from "../lib/chat-map";
import type { ChatThread as Thread } from "../hooks/useChatThread";
import { EditMessageModal } from "./EditMessageModal";

/**
 * Bir katmanın Escape'i yuvaya ULAŞMASIN — `ChatLauncherHost`'taki `stopEscapeAtLayer`'ın aynısı:
 * kitin `ChatLauncher`'ı Escape'i `document` üzerinde dinliyor ve düzenleme penceresini kapatan
 * Escape yuvayı da kapatırdı. Radix kendi Escape'ini yakalama evresinde zaten işledi.
 */
function stopEscape(event: ReactKeyboardEvent): void {
  if (event.key === "Escape") event.stopPropagation();
}

/**
 * The row of one message in the panel — found by WALKING the kit's `data-chat-message-id` hooks
 * and comparing, never by building a selector from an id (the kit's own `jumpTo` does the same,
 * for the same reason: a selector built from server data can throw).
 */
function messageRowIn(host: HTMLElement, messageId: string): HTMLElement | undefined {
  return Array.from(host.querySelectorAll<HTMLElement>("[data-chat-message-id]")).find(
    (node) => node.dataset.chatMessageId === messageId,
  );
}

/** The nearest ancestor of `node` (inside `host`) that actually scrolls — the panel's message column. */
function scrollerOf(node: HTMLElement, host: HTMLElement): HTMLElement | null {
  for (let current = node.parentElement; current && current !== host; current = current.parentElement) {
    const overflow = getComputedStyle(current).overflowY;
    if ((overflow === "auto" || overflow === "scroll") && current.scrollHeight > current.clientHeight) return current;
  }
  return null;
}

/**
 * One conversation, drawn.
 *
 * IT OWNS NO STATE. Everything comes from `useChatThread`, which both the full-screen page and
 * the dock call — so the two surfaces cannot disagree about what a failed send looks like, which
 * way the history runs, or when the read cursor moves. What differs between them is a height and
 * a header, and those are props.
 *
 * NOTHING IS DISABLED HERE EITHER. When the composer may not be used, it is closed with the
 * reason written under it — that is `ChatComposer`'s `disabledReason` contract, and the reason
 * comes from `composerBlockReason`, which keeps "arşivlendi", "yetkiniz yok" and "destek
 * oturumu" as three separate sentences because they have three separate fixes.
 */

export function ChatThread({
  thread,
  actions,
  reserveKey,
  emptyAction,
  chrome,
}: {
  thread: Thread;
  actions?: ReactNode;
  /** Reserves the panel's measured height across reloads, so a refresh does not collapse it. */
  reserveKey?: string | undefined;
  emptyAction?: ReactNode;
  /**
   * ÇERÇEVEYİ KİM ÇİZİYOR.
   *
   * Sayfada konuşma kendi kutusudur: kenarlık ve yarıçap onun. Yuvada ise zaten bir panelin
   * içinde duruyor — orada ikinci bir yuvarlak kutu, kutu içinde kutu olarak okunuyor ve dört
   * köşesi panelin köşelerine teğet geçiyordu. `flush` çerçeve sınıflarını EZMİYOR, kit onları
   * hiç yazmıyor (`@altek/ui` v3.10.0).
   */
  chrome?: "framed" | "flush";
}) {
  const conversation = thread.conversation;
  const { attachments } = thread;
  const hostRef = useRef<HTMLDivElement | null>(null);

  /**
   * ARAMA SONUCUNDAN GELİNEN MESAJA GİT (H6, 20 Ekim 2026). Kanca mesaj ekrana gelene kadar eski
   * sayfaları yüklüyor; ekrandaysa burası onu ortaya kaydırıyor. Kitin paneli bunun için bir prop
   * almıyor (kit isteği: `ChatPanel.focusMessageId`); alıntıya tıklamanın kullandığı aynı
   * `data-chat-message-id` kancasından bulunuyor. Bir kare beklenerek: panel kendi ilk «en alta
   * in» kaydırmasını yapmış olsun, bizimki onun üstüne yazsın.
   */
  const jumpTargetId = thread.jumpTargetId;
  const jumpDone = thread.jumpDone;
  useEffect(() => {
    if (!jumpTargetId) return;
    const frame = requestAnimationFrame(() => {
      const host = hostRef.current;
      const row = host ? messageRowIn(host, jumpTargetId) : undefined;
      if (host && row) {
        row.scrollIntoView({ block: "center" });
        /**
         * THE PANEL IS TOLD AT ONCE that the reader left the bottom. It learns it from its own
         * scroll handler, but the browser dispatches that event a frame later — and in between, its
         * ResizeObserver (the thread was still settling after the click) re-pinned to the newest
         * message: measured, the row ended 796 px above the viewport. A scroll event on the same
         * container runs the panel's own handler now; nothing of the kit is overridden.
         */
        scrollerOf(row, host)?.dispatchEvent(new Event("scroll"));
      }
      jumpDone();
    });
    return () => cancelAnimationFrame(frame);
  }, [jumpTargetId, jumpDone]);

  /**
   * DÜZENLE · SİL · YANITLA (H5). Kit üçünü mesajın altındaki satırda yazılı düğmeler olarak
   * çiziyor — üzerine gelince ortaya çıkan bir menü değil: dokunmatik ekranda da klavyede de
   * erişiliyor. «düzenle» ve «sil» yalnız kişinin KENDİ mesajında çiziliyor (kitin kuralı, sunucunun
   * da kuralı); «yanıtla» herkesinkinde. Yazma kutusu kapalıyken (arşivli grup, yetki yok, destek
   * oturumu) hiçbiri verilmiyor: sonucu yine bir ret olurdu.
   */
  const writable = thread.blockReason === null;

  /**
   * A group is rendered as the kit's `channel`, and a DM as `direct`.
   *
   * The kit's panel knows two shapes — a hash for a named room, the other person's avatar for a
   * one-to-one — and a group of nine people is the first of those. Calling it `direct` would put
   * one arbitrary member's photograph at the head of a nine-way conversation.
   */
  const isDirect = conversation?.kind === "DIRECT";

  /**
   * Rehber adresi yalnız GERÇEK bir karşı taraf varken üretiliyor: kendiyle konuşmada `peer`
   * okuyanın kendisi oluyor ve o bağlantı kendi satırına giderdi.
   */
  const peerId = thread.peer?.id ?? null;
  const peerHref =
    isDirect && peerId !== null && peerId !== thread.meId
      ? `/directory?record=${encodeURIComponent(peerId)}`
      : null;

  const subtitle = conversation
    ? conversation.archived
      ? "Arşivlendi"
      : conversation.kind === "GROUP"
        ? `${formatCount(conversation.memberCount)} üye`
        : undefined
    : undefined;

  const composer = (
    <div className="flex flex-col gap-2">
      {attachments.rejection ? (
        <Banner tone="warning" title="Dosya eklenmedi" onDismiss={attachments.dismissRejection}>
          {attachments.rejection}
        </Banner>
      ) : null}

      {/**
       * A SEND THIS CLIENT REFUSED SAYS SO, right where the text still is.
       *
       * `useChatThread` checks the server's own rules before the request — a body of 1–8000
       * characters (§14b), and no send while a file is still going up. Held in the hook and drawn
       * nowhere, those refusals were SILENT: the person pressed Enter on an attachment with no
       * text, nothing happened, and nothing said why. A banner rather than a toast, because the
       * fix is in the composer the banner sits on and a toast would be gone before they looked.
       */}
      {thread.notice ? (
        <Banner tone="warning" title="Mesaj gönderilmedi" onDismiss={thread.dismissNotice}>
          {thread.notice}
        </Banner>
      ) : null}

      <ComposerField thread={thread} />
    </div>
  );

  const panel = (
    <ChatPanel
      /**
       * Başlık `conversationTitle`'dan geliyor, doğrudan `title` alanından değil: kendiyle
       * konuşmanın adı kişinin kendi adı + «(Siz)», ve bu ek rayda da panelde de aynı yerden
       * geliyor. İki yüzey kendi başına okusaydı biri ekli biri eksiz olurdu.
       */
      title={conversationTitle(conversation, thread.meId) ?? "Sohbet"}
      kind={isDirect ? "direct" : "channel"}
      {...(thread.peer ? { peer: thread.peer } : {})}
      {...(isDirect ? {} : { participants: thread.members })}
      {...(subtitle ? { subtitle } : {})}
      messages={thread.messages}
      currentUserId={thread.meId}
      isLoading={thread.isLoading}
      error={thread.error}
      onRetryLoad={thread.retryLoad}
      {...(thread.firstUnreadId ? { firstUnreadId: thread.firstUnreadId } : {})}
      /**
       * «OKUNDU», YALNIZ BİREBİR SOHBETTE (KARARLAR K6). Sunucu birebir konuşmanın detayında
       * karşı tarafın imlecini veriyor (`counterpartRead`); kit bu imleçten önceki kendi
       * mesajlarımıza tik, en sonuncusuna «Okundu» yazıyor. Grupta prop hiç verilmiyor — kit
       * `channel`/`group` için zaten yok sayıyor, ama vermemek kuralı burada da görünür kılıyor:
       * grupta kimin okuduğu YAYILMIYOR, dolayısıyla gösterilecek bir şey de yok.
       */
      {...(isDirect && conversation?.counterpartRead?.lastReadMessageId
        ? { peerReadUpToId: conversation.counterpartRead.lastReadMessageId }
        : {})}
      typingUsers={thread.typingUsers}
      onLoadOlder={thread.loadOlder}
      isLoadingOlder={thread.isLoadingOlder}
      hasMoreOlder={thread.hasMoreOlder}
      onDownload={thread.download}
      /**
       * RESİM EKİ DE AÇILIYOR — şimdilik indirme olarak.
       *
       * Kit `image/*` eki önizleme karosu olarak çiziyor ve karoya basmak YALNIZ `onPreview`'i
       * çağırıyor. Verilmediğinde karo tıklanınca hiçbir şey yapmıyordu: resim ne görünüyor ne
       * indiriliyordu. Karoya küçük resim adresi veremiyoruz (imzalı adres `attachment` zorluyor,
       * `<img>`'de çalışmaz); üyelik denetimli satır içi uç gelene kadar (denetim Sohbet 19)
       * karo, belge ekiyle aynı imzalı indirmeyi açıyor.
       */
      onPreview={thread.download}
      onRetryMessage={thread.retryMessage}
      {...(reserveKey ? { reserveKey } : {})}
      {...(chrome ? { chrome } : {})}
      /**
       * BAŞLIK KİŞİ REHBERİNE GİDİYOR (birebir konuşmada).
       *
       * Karşındaki kişinin adına basınca rehberde onun çekmecesi açılıyor — `/directory` o
       * parametreyi zaten böyle okuyor. Kendinle konuşmada bağlantı YOK: kendi kartına gitmek
       * için «Profilim» var ve rehberde kendi satırına düşmek, okuyanın beklediği yer değil.
       *
       * `href`, `onClick` değil: orta tıklama ve ⌘-tıklama rehberi yeni sekmede açabiliyor. Kit
       * `titleHref` ile `<a>` çiziyor (v3.13.0).
       */
      {...(peerHref ? { titleHref: peerHref } : {})}
      {...(writable
        ? {
            onReplyMessage: thread.startReply,
            onEditMessage: thread.requestEdit,
            onDeleteMessage: thread.requestDelete,
          }
        : {})}
      composer={composer}
      {...(actions ? { actions } : {})}
      {...(emptyAction ? { emptyAction } : {})}
    />
  );

  return (
    // `contents`: kutu çizmiyor, panelin yerleşimine dokunmuyor — yalnız mesaj satırlarını bulmak ve
    // katmanların Escape'ini durdurmak için bir kök.
    <div ref={hostRef} className="contents">
      {panel}
      <div className="contents" onKeyDown={stopEscape}>
        {thread.editing ? (
          <EditMessageModal
            key={thread.editing.messageId}
            initialBody={thread.editing.body}
            isPending={thread.isSavingEdit}
            onCancel={thread.cancelEdit}
            onSave={thread.saveEdit}
          />
        ) : null}
        <ConfirmDialog
          open={thread.deleting !== null}
          onOpenChange={(open) => {
            if (!open && !thread.isDeleting) thread.cancelDelete();
          }}
          title="Mesajı silmek istiyor musunuz?"
          impact="Mesaj ve ekleri sohbetteki herkes için kaldırılır; yerinde «Bu mesaj silindi» yazar. Bu işlem geri alınamaz."
          confirmLabel="Mesajı sil"
          destructive
          isPending={thread.isDeleting}
          onConfirm={thread.confirmDelete}
        />
      </div>
    </div>
  );
}

/**
 * YAZMA KUTUSU, taslağa KENDİSİ abone.
 *
 * Denetim Sohbet 15, ölçüldü: taslak sayfanın durumundayken her tuş sayfanın tamamını — 300
 * mesajlık paneli ve konuşma listesini — yeniden çiziyordu (5 tuşta liste 5, panel 5 çizim).
 * Metin artık `thread.drafts` deposunda ve yalnız bu küçük bileşen onu okuyor; tuş vuruşu
 * yalnız burayı çiziyor (`chat-render-cost.test.tsx`).
 */
function ComposerField({ thread }: { thread: Thread }) {
  const conversationId = thread.conversationId ?? "";
  const value = useSyncExternalStore(
    thread.drafts.subscribe,
    () => thread.drafts.get(conversationId),
    () => "",
  );
  const { attachments } = thread;

  return (
    <ChatComposer
      value={value}
      onChange={thread.onDraftChange}
      onSend={thread.send}
      uploads={attachments.uploads}
      onAttach={attachments.attach}
      onCancelUpload={attachments.cancel}
      onRetryUpload={attachments.retry}
      mentionCandidates={thread.members}
      /**
       * YANIT ŞERİDİ (H5): «yanıtla»ya basılan mesajın başı yazma kutusunun üstünde; çarpı ya da
       * Escape kaldırıyor. Gönderim `replyToMessageId` taşıyor, gönderilince şerit kapanıyor.
       */
      replyTo={thread.replyTo}
      onCancelReply={thread.cancelReply}
      isSending={thread.isSending}
      disabled={thread.blockReason !== null}
      {...(thread.blockReason ? { disabledReason: thread.blockReason } : {})}
    />
  );
}

/**
 * The right-hand pane before a conversation has been chosen.
 *
 * `no-data` and not `no-results`: nothing is filtered here — the reader simply has not opened
 * anything yet, and telling them their search matched nothing would be false.
 *
 * NOT `Card`, AND THAT IS DELIBERATE. `Card` is the kit's SHADOWED surface (`shadow-e1`), and
 * this box stands where `ChatPanel` stands a moment later — which declares the same hairline and
 * radius with NO shadow, in the kit's own file. Wrapping this in a `Card` would make the pane
 * lift off the page while a conversation is being chosen and settle flat once one is open, so
 * the frame follows the panel it replaces rather than the component with the closest name.
 */
export function ChatThreadPlaceholder({
  action,
  description,
  framed = true,
}: {
  action?: ReactNode;
  /**
   * Yuvada liste GİZLENEBİLİYOR, ve gizliyken "soldaki listeden seçin" demek okuyanı boş bir
   * yere bakmaya gönderir. Metin o yüzden çağıranın.
   */
  description?: ReactNode;
  /**
   * Sayfada bu kutu kendi çerçevesini çiziyor; yuvada panelin çerçevesi zaten var ve ikincisi
   * kutu içinde kutu olarak okunur.
   */
  framed?: boolean;
}) {
  const body = (
    <EmptyState
      variant="no-data"
      size={framed ? "md" : "sm"}
      title="Bir sohbet seçin"
      description={
        description ??
        "Soldaki listeden bir kişi ya da grup seçin; yeni bir sohbeti de buradan başlatabilirsiniz."
      }
      {...(action ? { action } : {})}
    />
  );

  return framed ? (
    <div className="grid h-full place-items-center rounded-lg border border-border bg-surface p-6">
      {body}
    </div>
  ) : (
    <div className="grid h-full place-items-center p-4">{body}</div>
  );
}
